import React, { useState, useRef, use, useEffect } from "react";
import {
  Card,
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";
import NumberFormat from "react-number-format";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api/Middleware";
import { ACCOUNT_URL, USERS_URL } from "../api/Urls";
import CustomWizard from "../jsx/components/Forms/Wizard/Custom";
import ModalComponent from "../jsx/components/Modal";
import TermsOfUse from "../jsx/components/ModalData/TermsOfUse";
import { setCookie } from "nookies";
import jwt_decode from "jwt-decode";
import MoonLoader from "react-spinners/MoonLoader";
import styled from "styled-components";
import Swal from "sweetalert2";

// Lista de categorias de negócio por segmento
const businessCategoriesBySegment = {
  1: [
    { value: "corretor", label: "Corretor" },
    { value: "imobiliaria", label: "Imobiliária" },
    { value: "administracao", label: "Administradora de Imóveis" },
  ],
  2: [
    // Saúde e Bem estar
    { value: "clinica_medica", label: "Clínica Médica" },
    { value: "odontologia", label: "Odontologia" },
    { value: "fisioterapia", label: "Fisioterapia" },
    { value: "psicologia", label: "Psicologia" },
    { value: "clinica_optometrista", label: "Optometria" },
    { value: "nutricao", label: "Nutrição" },
    { value: "farmacia", label: "Farmácia" },
    { value: "academia", label: "Academia" },
    { value: "yoga", label: "Yoga" },
    { value: "pilates", label: "Pilates" },
    { value: "estetica", label: "Estética" },
    { value: "spa", label: "SPA" },
    { value: "personal_trainer", label: "Personal Trainer" },
    { value: "medicina_alternativa", label: "Medicina Alternativa" },
    { value: "terapias_holísticas", label: "Terapias Holísticas" },
    { value: "suplementos", label: "Suplementos" },
  ],
  3: [
    // Varejo
    { value: "moda", label: "Moda e Vestuário" },
    { value: "calcados", label: "Calçados" },
    { value: "acessorios", label: "Acessórios" },
    { value: "joias", label: "Joias e Bijuterias" },
    { value: "eletronicos", label: "Eletrônicos" },
    { value: "informatica", label: "Informática" },
    { value: "moveis", label: "Móveis" },
    { value: "decoracao", label: "Decoração" },
    { value: "livros", label: "Livros e Papelaria" },
    { value: "cosmeticos", label: "Cosméticos" },
    { value: "bebidas", label: "Bebidas" },
    { value: "supermercado", label: "Supermercado" },
    { value: "mercado", label: "Mercado de Bairro" },
    { value: "pet_shop", label: "Pet Shop" },
    { value: "brinquedos", label: "Brinquedos" },
  ],
  4: [
    // Gastronomia
    { value: "restaurante", label: "Restaurante" },
    { value: "fast_food", label: "Fast Food" },
    { value: "pizzaria", label: "Pizzaria" },
    { value: "lanchonete", label: "Lanchonete" },
    { value: "cafeteria", label: "Cafeteria" },
    { value: "confeitaria", label: "Confeitaria" },
    { value: "padaria", label: "Padaria" },
    { value: "sorveteria", label: "Sorveteria" },
    { value: "acougue", label: "Açougue" },
    { value: "bar", label: "Bar" },
    { value: "pub", label: "Pub" },
    { value: "delivery", label: "Delivery" },
    { value: "comida_japonesa", label: "Comida Japonesa" },
    { value: "comida_italiana", label: "Comida Italiana" },
    { value: "comida_brasileira", label: "Comida Brasileira" },
  ],
};

const TermsOfUseButton = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button
        variant="info"
        size="sm"
        className="btn mt-3"
        onClick={() => setShowModal(true)}
      >
        Ler os termos de uso
      </Button>

      {showModal && (
        <ModalComponent
          props={{
            title: "Termos de uso do Sistema",
            content: <TermsOfUse setShowModal={setShowModal} />,
            showModal,
            setShowModal,
            showFooter: false,
            size: "xl",
          }}
        />
      )}
    </>
  );
};

// Componente do passo 1 - Cadastro da Conta
const AccountStep = ({ onNext }) => {
  const [personType, setPersonType] = useState(1); // 1 = PJ, 2 = PF
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const accountData = localStorage.getItem("accountData");

    if (accountData) {
      onNext();
    }
  }, []);

  // Validação do primeiro passo - Conta da empresa
  const accountSchema = yup.object({
    company_name: yup.string().required("Nome da empresa é obrigatório"),
    cpf_cnpj: yup
      .string()
      .required("CPF/CNPJ é obrigatório")
      .test(
        "len",
        "CPF deve ter 11 dígitos ou CNPJ 14 dígitos",
        (val) => !val || val.length === 11 || val.length === 14
      ),
    company_type:
      personType === 1
        ? yup.string().required("Tipo de empresa é obrigatório")
        : yup.string(),
    email: yup
      .string()
      .email("E-mail inválido")
      .required("E-mail é obrigatório"),
    phone: yup.string().required("Telefone é obrigatório"),
    business_segment: yup
      .string()
      .required("Segmento de negócio é obrigatório"),
    business_categories: yup
      .array()
      .min(1, "Selecione pelo menos uma categoria")
      .required("Categorias são obrigatórias"),
    status: yup.number().required("Status é obrigatório"),
    terms_of_use: yup
      .boolean()
      .oneOf([true], "É necessário aceitar os termos de uso")
      .required("É necessário aceitar os termos de uso"),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(accountSchema),
    defaultValues: {
      company_name: "",
      person_type: 1,
      cpf_cnpj: "",
      company_type: "LIMITED",
      email: "",
      phone: "",
      business_segment: "",
      business_categories: [],
      status: 1,
      terms_of_use: false,
    },
  });

  // Função para formatar CPF/CNPJ
  const formatCpfCnpj = (value) => {
    if (!value) return "";

    // Remove todos os caracteres não numéricos
    const numericValue = value.replace(/\D/g, "");

    // Limita ao tamanho máximo (14 para CNPJ)
    return numericValue.slice(0, personType === 1 ? 14 : 11);
  };

  // Função para formatar telefone
  const formatPhone = (value) => {
    if (!value) return "";

    // Remove todos os caracteres não numéricos
    const numericValue = value.replace(/\D/g, "");

    // Limita ao tamanho máximo (11 para telefone com DDD)
    return numericValue.slice(0, 11);
  };

  // Handler para o envio do primeiro passo
  const handleAccountSubmit = async (data) => {
    try {
      setLoading(true);
      setError("");

      // Formata os dados antes de enviar
      const formattedData = {
        ...data,
        person_type: personType,
        business_categories: data.business_categories.map((cat) => cat.value),
        terms_of_use: data.terms_of_use ? 1 : 0,
      };

      const response = await api.post(ACCOUNT_URL, formattedData);
      localStorage.setItem("accountData", JSON.stringify(response.data));
      onNext();
    } catch (err) {
      setError(
        err.response?.data?.message || "Ocorreu um erro ao criar a conta"
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form noValidate onSubmit={handleSubmit(handleAccountSubmit)}>
      <h5 className="mb-4">Informações da Empresa</h5>

      <Form.Group className="mb-3">
        <Form.Label className="font-weight-bold">Tipo de Pessoa:</Form.Label>
        <div className="d-flex">
          <div className="form-check mr-3">
            <input
              className="form-check-input"
              type="radio"
              id="pj"
              checked={personType === 1}
              onChange={() => setPersonType(1)}
            />
            <label className="form-check-label" htmlFor="pj">
              Pessoa Jurídica
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="pf"
              checked={personType === 2}
              onChange={() => setPersonType(2)}
            />
            <label className="form-check-label" htmlFor="pf">
              Pessoa Física
            </label>
          </div>
        </div>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          {personType === 1 ? "Nome da Empresa *" : "Nome Completo *"}
        </Form.Label>
        <Form.Control
          type="text"
          isInvalid={!!errors.company_name}
          {...register("company_name")}
        />
        <Form.Control.Feedback type="invalid">
          {errors.company_name?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{personType === 1 ? "CNPJ *" : "CPF *"}</Form.Label>
        <Controller
          control={control}
          name="cpf_cnpj"
          render={({ field }) => (
            <NumberFormat
              className={`form-control ${errors.cpf_cnpj ? "is-invalid" : ""}`}
              format={
                personType === 1 ? "##.###.###/####-##" : "###.###.###-##"
              }
              mask="_"
              value={field.value}
              onValueChange={(values) => {
                field.onChange(formatCpfCnpj(values.value));
              }}
              placeholder={personType === 1 ? "Digite o CNPJ" : "Digite o CPF"}
            />
          )}
        />
        <Form.Control.Feedback type="invalid">
          {errors.cpf_cnpj?.message}
        </Form.Control.Feedback>
      </Form.Group>

      {personType === 1 && (
        <Form.Group className="mb-3">
          <Form.Label>Tipo de Empresa *</Form.Label>
          <Form.Select
            isInvalid={!!errors.company_type}
            {...register("company_type")}
          >
            <option value="">Selecione o tipo</option>
            <option value="MEI">Micro Empreendedor Individual</option>
            <option value="LIMITED">Empresa Limitada</option>
            <option value="INDIVIDUAL">Empresa Individual</option>
            <option value="ASSOCIATION">Associação</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {errors.company_type?.message}
          </Form.Control.Feedback>
        </Form.Group>
      )}

      <Form.Group className="mb-3">
        <Form.Label>E-mail para contato*</Form.Label>
        <Form.Control
          type="email"
          isInvalid={!!errors.email}
          {...register("email")}
        />
        <Form.Control.Feedback type="invalid">
          {errors.email?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Telefone *</Form.Label>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <NumberFormat
              className={`form-control ${errors.phone ? "is-invalid" : ""}`}
              format="(##) #####-####"
              mask="_"
              value={field.value}
              onValueChange={(values) => {
                field.onChange(formatPhone(values.value));
              }}
              placeholder="Digite o telefone"
            />
          )}
        />
        <Form.Control.Feedback type="invalid">
          {errors.phone?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Segmento de Negócio *</Form.Label>
        <Controller
          control={control}
          name="business_segment"
          render={({ field }) => (
            <Form.Select
              className={errors.business_segment ? "is-invalid" : ""}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value);
                // Reset business_categories when segment changes
                setValue("business_categories", []);
              }}
            >
              <option value="">Selecione um segmento</option>
              <option value="1">Imobiliário</option>
              <option value="2">Saúde e Bem estar</option>
              <option value="3">Varejo</option>
              <option value="4">Gastronomia</option>
            </Form.Select>
          )}
        />
        <Form.Control.Feedback type="invalid">
          {errors.business_segment?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Categorias de Negócio *</Form.Label>
        <Controller
          control={control}
          name="business_categories"
          render={({ field }) => (
            <Select
              isMulti
              options={
                watch("business_segment")
                  ? businessCategoriesBySegment[watch("business_segment")]
                  : []
              }
              className={errors.business_categories ? "is-invalid" : ""}
              value={field.value}
              onChange={field.onChange}
              isDisabled={!watch("business_segment")}
              placeholder="Selecione as categorias"
            />
          )}
        />
        {errors.business_categories && (
          <div className="invalid-feedback d-block">
            {errors.business_categories.message}
          </div>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          id="terms-checkbox"
          label="Aceito os termos de uso e política de privacidade"
          isInvalid={!!errors.terms_of_use}
          {...register("terms_of_use")}
        />
        {errors.terms_of_use && (
          <div className="invalid-feedback d-block">
            {errors.terms_of_use.message}
          </div>
        )}
        <TermsOfUseButton />
      </Form.Group>

      <div className="d-grid gap-2 mt-4">
        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? "Enviando..." : "Continuar"}
        </Button>
      </div>
    </Form>
  );
};

// Componente do passo 2 - Cadastro do Usuário
const UserStep = ({ onPrevious, setRedirecting }) => {
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Validação do segundo passo - Usuário
  const userSchema = yup.object({
    first_name: yup.string().required("Nome é obrigatório"),
    last_name: yup.string(),
    cpf: yup
      .string()
      .required("CPF é obrigatório")
      .test(
        "len",
        "CPF deve ter 11 dígitos",
        (val) => !val || val.length === 11
      ),
    email: yup
      .string()
      .email("E-mail inválido")
      .required("E-mail é obrigatório"),
    confirmEmail: yup
      .string()
      .email("E-mail inválido")
      .oneOf([yup.ref("email")], "Os emails não conferem")
      .required("Confirme seu e-mail"),
    phone: yup.string().required("Telefone é obrigatório"),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: {
      first_name: "",
      cpf: "",
      email: "",
      confirmEmail: "",
      phone: "",
    },
  });

  // Função para formatar CPF
  const formatCpf = (value) => {
    if (!value) return "";

    // Remove todos os caracteres não numéricos
    const numericValue = value.replace(/\D/g, "");

    // Limita ao tamanho máximo (11 para CPF)
    return numericValue.slice(0, 11);
  };

  // Função para formatar telefone
  const formatPhone = (value) => {
    if (!value) return "";

    // Remove todos os caracteres não numéricos
    const numericValue = value.replace(/\D/g, "");

    // Limita ao tamanho máximo (11 para telefone com DDD)
    return numericValue.slice(0, 11);
  };

  // Handler para o envio do segundo passo
  const handleUserSubmit = async (data) => {
    try {
      setLoading(true);
      setError("");

      //TODO: TEM QUE VALIDAR O CPF E TELEFONE SE JÁ EXISTE NO BANCO
      const accountData = JSON.parse(
        localStorage.getItem("accountData") || "{}"
      );

      const userData = {
        ...data,
        account: accountData.id,
      };

      delete userData.confirmEmail;

      const response = await api.post(
        USERS_URL + "create_first_admin/",
        userData
      );
      setRedirecting(true);

      setCookie(undefined, "token", response.data.access, {
        maxAge: 60 * 60 * 24 * 7,
      });
      const decodedUser = jwt_decode(response.data.access);
      const cleanedUser = { ...decodedUser };
      delete cleanedUser.access;
      delete cleanedUser.jti;
      delete cleanedUser.exp;
      delete cleanedUser.token_type;

      // Salvar e limpar dados
      localStorage.setItem("user", JSON.stringify(cleanedUser));
      localStorage.removeItem("accountData");

      setTimeout(() => {
        window.location.href = "/";
      }, 2500);
    } catch ({ response }) {
      if (response.status === 400) {
        if (response.data?.phone) {
          Swal.fire({
            title: "Oops!",
            html: "Este telefone já está cadastrado!",
            icon: "warning",
            toast: true,
            position: "bottom-end",
            timer: 2500,
          });
        } else if (response.data?.cpf) {
          Swal.fire({
            title: "Oops!",
            html: "Este CPF já está cadastrado!",
            icon: "warning",
            toast: true,
            position: "bottom-end",
            timer: 2500,
          });
        }
      } else {
        Swal.fire({
          title: "Oops!",
          text: "Ocorreu um erro ao tentar finalizar seu cadastro! Por favor, tente novamente, ou entre em contato conosco.",
          icon: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form noValidate onSubmit={handleSubmit(handleUserSubmit)}>
      <h4 className="text-center mb-5">Informações do Usuário Administrador</h4>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Nome *</Form.Label>
            <Form.Control
              type="text"
              isInvalid={!!errors.first_name}
              {...register("first_name")}
            />
            <Form.Control.Feedback type="invalid">
              {errors.first_name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Sobrenome</Form.Label>
            <Form.Control
              type="text"
              isInvalid={!!errors.last_name}
              {...register("last_name")}
            />
            <Form.Control.Feedback type="invalid">
              {errors.last_name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Telefone *</Form.Label>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <NumberFormat
                  className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                  format="(##) #####-####"
                  mask="_"
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(formatPhone(values.value));
                  }}
                  placeholder="Digite o telefone"
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phone?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>CPF *</Form.Label>
            <Controller
              control={control}
              name="cpf"
              render={({ field }) => (
                <NumberFormat
                  className={`form-control ${errors.cpf ? "is-invalid" : ""}`}
                  format="###.###.###-##"
                  mask="_"
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(formatCpf(values.value));
                  }}
                  placeholder="Digite o CPF"
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.cpf?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>E-mail *</Form.Label>
            <Form.Control
              type="email"
              isInvalid={!!errors.email}
              {...register("email")}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Confirme seu E-mail *</Form.Label>
            <Form.Control
              type="email"
              isInvalid={!!errors.confirmEmail}
              {...register("confirmEmail")}
            />
            <Form.Control.Feedback type="invalid">
              {errors.confirmEmail?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <div className="d-flex justify-content-between mt-4">
        <Button
          variant="outline-secondary"
          onClick={onPrevious}
          disabled={loading}
        >
          Voltar
        </Button>
        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? "Enviando..." : "Concluir Cadastro"}
        </Button>
      </div>
    </Form>
  );
};

const Register = () => {
  const [searchParams] = useSearchParams();
  const liberacao = searchParams.get("liberacao");
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const wizardRef = useRef();

  if (!liberacao || liberacao !== "weJ8QtSjkcRsYKxuaSLKP3ZGtHsM78mb50lnanMa") {
    return (window.location.href = "/login");
  }

  const steps = [
    {
      name: "Empresa",
      component: <AccountStep onNext={() => wizardRef.current.nextStep()} />,
    },
    {
      name: "Usuário",
      component: (
        <UserStep
          onPrevious={() => wizardRef.current.previousStep()}
          setRedirecting={setRedirecting}
        />
      ),
    },
  ];

  return (
    <div className="main-container">
      <Row className="justify-content-center">
        {redirecting && (
          <Redirecting lg={12}>
            <MoonLoader color="#fff" loading={true} />
            <h4 className="text-white text-center px-2">
              Você está sendo redirecionado para seu painel...
            </h4>
          </Redirecting>
        )}

        <Col md={8} lg={7}>
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4">
              <h2 className="card-title mb-4 text-center">Dados da Conta</h2>
              {error && <Alert variant="danger">{error}</Alert>}

              <CustomWizard ref={wizardRef} steps={steps} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const Redirecting = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

export default Register;
